<script setup lang="tsx">
import { moduleTypes } from '@/constants/module'
import { getModuleTypeText } from '@/services/utils'
import type { ModuleForm } from '@/types/module'
import InstanceSelect from '../widgets/InstanceSelect.vue'
import ResponsibleSelect from '../widgets/ResponsibleSelect.vue'
import { getStatus, getStatusIndicator } from './shared'
import { computed } from 'vue'
import URLReference from '../comment_references/URLReference.vue'
import DocumentReference from '../comment_references/DocumentReference.vue'
import { ModuleReference } from '@/models/enums-generated'

const props = defineProps<{
	canEditInstance?: boolean
	mode: 'add' | 'edit' | 'view'
}>()

const model = defineModel<ModuleForm>({ required: true })

const addMode = computed(() => props.mode === 'add')
const viewMode = computed(() => props.mode === 'view')
</script>

<template>
	<div :class="{ striped: viewMode }">
		<div class="form-group row mb-2">
			<label for="description" class="col-6 col-form-label">{{ $t('description') }}</label>
			<div class="col-6 text-end">
				<span v-if="viewMode || !model.isCustom" data-test="module-view-description">
					{{ model.description || '-' }}
				</span>
				<textarea
					v-else
					class="form-control"
					id="module-description"
					data-test="module-form-description"
					v-model="model.description"
					maxlength="200"
					required
				/>
			</div>
		</div>

		<div class="form-group row mb-2">
			<label for="name" class="col-6 col-form-label">{{ $t('code') }}</label>
			<div class="col-6 text-end">
				<span v-if="viewMode || !model.isCustom" data-test="module-view-name">
					{{ model.name || '-' }}
				</span>
				<input
					v-else
					type="text"
					class="form-control"
					id="module-name"
					data-test="module-form-name"
					v-model="model.name"
					maxlength="10"
					required
				/>
			</div>
		</div>

		<div class="form-group row mb-2">
			<label for="name" class="col-6 col-form-label">{{ $t('version') }}</label>
			<div class="col-6 text-end">
				<span>{{ $te(model.version) ? $t(model.version) : model.version || '-' }}</span>
			</div>
		</div>

		<div class="form-group row mb-2">
			<label for="module-type" class="col-6 col-form-label">{{ $t('module.type') }}</label>
			<div class="col-6 text-end">
				<select v-if="addMode" v-model="model.type" id="module-type" data-test="module-form-type" class="form-select" required>
					<option v-for="option in moduleTypes" :key="option" :value="option">
						{{ getModuleTypeText(option) }}
					</option>
				</select>
				<span v-else data-test="module-view-type">{{ getModuleTypeText(model.type) || '-' }}</span>
			</div>
		</div>

		<div v-if="canEditInstance" class="form-group row mb-2">
			<label for="instance" class="col-6 col-form-label">{{ $t('instance') }}</label>
			<div class="col-6 text-end">
				<span v-if="viewMode || !canEditInstance" data-test="module-view-instance">
					{{ model.instance.name || '-' }}
				</span>
				<InstanceSelect v-else id="module-form-instance" data-test="module-form-instance" v-model="model.instance" class="mb-2" />
			</div>
		</div>

		<div class="form-group row mb-2">
			<label class="col-6 col-form-label">{{ $t('responsible') }}</label>
			<div class="col-6 text-end">
				<span v-if="viewMode" data-test="module-view-responsible">
					{{ model.responsible?.label || '-' }}
				</span>
				<ResponsibleSelect v-else v-model="model.responsible" id="module-responsible" dataTest="module-form-responsible" class="mb-2" />
			</div>
		</div>

		<div v-if="!addMode" class="form-group row mb-2">
			<label class="col-6 col-form-label" for="module-form-active">{{ $t('module.status.label') }}</label>
			<div class="col-6 d-flex">
				<div v-if="viewMode" class="align-self-center ms-auto">
					<component :is="getStatusIndicator(getStatus(model.active, model.isApplicable))" />
				</div>
				<div v-else class="form-check form-switch align-self-center ms-auto">
					<input
						v-model="model.active"
						class="form-check-input"
						type="checkbox"
						role="switch"
						id="module-form-active"
						data-test="module-form-active"
					/>
				</div>
			</div>
		</div>
	</div>
	<div>
		<div class="col">
			<h6>{{ $t('reference.label', 2) }}</h6>
		</div>
	</div>
	<hr class="mt-2 mb-2 border-1" />
	<div class="row">
		<div class="col-5">
			<URLReference v-model="model.urlReferences" :owner-type="ModuleReference" :owner-id="model.id" :can-edit="!viewMode" />
		</div>

		<div class="col-auto d-flex">
			<div class="vr mx-3"></div>
		</div>

		<div class="col-5">
			<DocumentReference v-model="model.documentReferences" :owner-type="ModuleReference" :owner-id="model.id" :can-edit="!viewMode" />
		</div>
	</div>
</template>
